/**
 * Manager pictures, available to every badge in the app without threading a store through them.
 *
 * `GmBadge` is drawn on nearly every screen — the draft board, ten team cards, every records
 * table — and takes nothing but a GM id. Giving each of those call sites a store prop to fetch a
 * picture with would be a hundred edits to show a face, so the faces arrive through context and
 * the badge asks for them. One subscription for the whole app.
 *
 * Absent is the normal case and never an error: nine managers will not upload anything and the
 * initials badge that has always been there stands in.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Avatar, LeagueStore } from '../store/types.js';

/** GM id (uppercase) -> data URL. */
const Ctx = createContext<ReadonlyMap<string, string>>(new Map());

export function AvatarProvider({ store, children }: { store: LeagueStore; children: ReactNode }) {
  const [rows, setRows] = useState<Avatar[]>([]);

  useEffect(() => {
    let live = true;
    const load = () => store.avatars()
      .then((a) => { if (live) setRows(a); })
      // A face is decoration. If the collection cannot be read — offline, rules not yet deployed
      // — every badge falls back to initials and nothing else on the screen notices.
      .catch(() => { if (live) setRows([]); });
    void load();
    const off = store.subscribe(0, (what) => { if (what === 'avatars') void load(); });
    return () => { live = false; off(); };
  }, [store]);

  const map = useMemo(
    () => new Map(rows.filter((a) => a.image).map((a) => [a.gm.toUpperCase(), a.image])),
    [rows],
  );

  return <Ctx.Provider value={map}>{children}</Ctx.Provider>;
}

/** The picture for one manager, or undefined when he has not set one. */
export function useAvatar(gm: string): string | undefined {
  return useContext(Ctx).get(gm.toUpperCase());
}

/** Every picture, for a screen that needs to know who has one. */
export function useAvatars(): ReadonlyMap<string, string> {
  return useContext(Ctx);
}

/**
 * An identity circle that shows a manager's photo when he has one and whatever glyph the caller
 * passes when he has not — the initials mark, on every screen that draws one.
 *
 * It exists because the alternative is `useAvatar` at the top of each component and a ternary at
 * each call site, and half the call sites are inside `.map()` over ten managers, where a hook
 * cannot go. One component per circle keeps the hook legal and the fallback identical everywhere.
 *
 * The class is the CALLER'S — `dolmark`, `dolclaimmark` — and `hasface` is appended to it, so the
 * host rule keeps its size, its border and its place in the layout; only the fill is handed over
 * to the picture (see the shared `.hasface` block in `styles.css`).
 */
export function Mark({ gm, className, children }: {
  gm: string; className: string; children: ReactNode;
}) {
  const face = useAvatar(gm);
  return (
    <span className={`${className}${face ? ' hasface' : ''}`} aria-hidden="true">
      {face ? <img src={face} alt="" draggable={false} /> : children}
    </span>
  );
}
